import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, matchDetails } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const currentSlug = slug || 'football';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];
  const matches = matchDetails[currentSlug as keyof typeof matchDetails] || [];

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${currentCategory.name} - 皇冠体育`,
    description: t('seo.category_desc'),
    url: `https://huangguan-sports.com/category/${currentSlug}`,
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}竞猜 - ${t('seo.category_title')}`}
        description={t('seo.category_desc')}
        keywords={t('seo.category_keywords')}
        canonicalUrl={`https://huangguan-sports.com/category/${currentSlug}`}
      />
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="px-4 md:px-6 py-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: t('category.title') }]} />

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory.name}{t('category.title')}
              </h1>
              <p className="text-foreground-500 text-sm">
                共 <strong className="text-primary-500">{currentCategory.count}</strong> 场赛事可供竞猜
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar - Category Filter */}
              <aside className="w-full lg:w-56 flex-shrink-0">
                <div className="bg-background-50 rounded-xl border border-background-200 p-3 lg:sticky lg:top-24">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3 px-2">{t('category.title')}</h3>
                  <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                          currentSlug === cat.slug
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        <span>{cat.name}</span>
                        <span className={`ml-auto text-xs ${currentSlug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                          {cat.count}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <section className="flex-1 min-w-0">
                {/* Tab Switcher */}
                <div className="flex items-center gap-1 mb-6 bg-background-100 rounded-full p-1 w-fit">
                  <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap">
                    {t('category.upcoming')}
                  </button>
                  <button className="px-5 py-2 rounded-full text-foreground-500 text-sm font-medium hover:text-foreground-700 transition-colors whitespace-nowrap">
                    {t('category.today')}
                  </button>
                  <button className="px-5 py-2 rounded-full text-foreground-500 text-sm font-medium hover:text-foreground-700 transition-colors whitespace-nowrap">
                    {t('category.live')}
                  </button>
                </div>

                {/* Match Table */}
                {matches.length > 0 ? (
                  <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                    {/* Table Header */}
                    <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-background-100 text-xs font-semibold text-foreground-500">
                      <div className="col-span-3">{t('category.league')}</div>
                      <div className="col-span-2">{t('category.time')}</div>
                      <div className="col-span-3 text-center">{t('category.home_team')}</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-3 text-center">{t('category.away_team')}</div>
                    </div>

                    {matches.map((match) => (
                      <div
                        key={match.id}
                        className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 py-4 border-b border-background-100 last:border-b-0 hover:bg-background-50 transition-colors items-center"
                      >
                        <div className="md:col-span-3 flex items-center gap-2">
                          <i className="ri-flag-2-line text-primary-400 text-sm"></i>
                          <span className="font-medium text-foreground-800 text-sm">{match.league}</span>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-1.5 text-sm text-foreground-500">
                          <i className="ri-time-line text-xs"></i>
                          <span>{match.time}</span>
                        </div>
                        <div className="md:col-span-3 text-center">
                          <span className="font-bold text-foreground-900 text-sm">{match.homeTeam}</span>
                        </div>
                        <div className="md:col-span-1 flex justify-center">
                          <span className="text-foreground-300 font-heading font-bold text-lg">VS</span>
                        </div>
                        <div className="md:col-span-3 text-center">
                          <span className="font-bold text-foreground-900 text-sm">{match.awayTeam}</span>
                        </div>

                        {/* Odds Row - Mobile */}
                        <div className="md:hidden grid grid-cols-3 gap-2 bg-background-100 rounded-lg p-3 mt-1">
                          <div className="text-center">
                            <span className="text-xs text-foreground-400 block mb-0.5">主胜</span>
                            <span className="text-primary-500 font-bold text-sm">{match.homeOdds.toFixed(2)}</span>
                          </div>
                          {match.drawOdds > 0 && (
                            <div className="text-center">
                              <span className="text-xs text-foreground-400 block mb-0.5">平局</span>
                              <span className="text-foreground-600 font-bold text-sm">{match.drawOdds.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="text-center">
                            <span className="text-xs text-foreground-400 block mb-0.5">客胜</span>
                            <span className="text-foreground-600 font-bold text-sm">{match.awayOdds.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Odds - Desktop */}
                        <div className="hidden md:flex md:col-span-12 items-center justify-center gap-4 mt-2 py-2 bg-background-50 rounded-lg">
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <span className="text-xs text-foreground-400 mr-2">主胜</span>
                              <span className="text-primary-500 font-bold">{match.homeOdds.toFixed(2)}</span>
                            </div>
                            {match.drawOdds > 0 && (
                              <div className="text-center">
                                <span className="text-xs text-foreground-400 mr-2">平局</span>
                                <span className="text-foreground-600 font-bold">{match.drawOdds.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="text-center">
                              <span className="text-xs text-foreground-400 mr-2">客胜</span>
                              <span className="text-foreground-600 font-bold">{match.awayOdds.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-background-50 rounded-xl border border-background-200 p-12 text-center">
                    <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                    <p className="text-foreground-500">{t('category.no_matches')}</p>
                  </div>
                )}
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}